import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { X, Calendar as CalendarIcon } from "lucide-react";
import { getBoardTypeConfig } from "@/lib/board-types";

interface TaskDueDatePickerProps {
  date: Date | undefined;
  onChange: (date: Date | undefined) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  canEdit: boolean;
  isEditing: boolean;
  isOverdue: boolean;
  dueDate: string | null;
  boardType?: string;
}

export function TaskDueDatePicker({
  date,
  onChange,
  open,
  onOpenChange,
  canEdit,
  isEditing,
  isOverdue,
  dueDate,
  boardType,
}: TaskDueDatePickerProps) {
  const config = getBoardTypeConfig(boardType);
  const dateLabel = config.labels.dueDate;
  const isMeeting = boardType === "meeting";
  return (
    <div className="space-y-2">
      <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1">
        <CalendarIcon className="w-3 h-3" /> {dateLabel}
      </Label>
      {isEditing && canEdit ? (
        <div className="flex items-center gap-2">
          <Popover open={open} onOpenChange={onOpenChange}>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" data-testid="task-button-edit-due-date">
                <CalendarIcon className="w-3 h-3 mr-1" />
                {date ? date.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" }) : "Set date"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0">
              <Calendar
                mode="single"
                selected={date}
                onSelect={(d) => { onChange(d); onOpenChange(false); }}
              />
            </PopoverContent>
          </Popover>
          {date && (
            <Button variant="ghost" size="sm" onClick={() => onChange(undefined)} data-testid="task-button-clear-due-date">
              <X className="w-3 h-3" /> Clear
            </Button>
          )}
        </div>
      ) : (
        <span
          className={`text-sm ${isOverdue && !isMeeting ? "text-destructive font-medium" : ""}`}
          data-testid="task-text-due-date"
        >
          {dueDate ? new Date(dueDate + "T00:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" }) : <span className="text-muted-foreground italic">{config.labels.noDueDate}</span>}
        </span>
      )}
    </div>
  );
}
